/**
 * 🛡 E-E-A-T AJANI — Deneyim, Uzmanlık, Otorite, Güven.
 *
 * İKİ AYRI İŞ, iki sekme:
 *  • Site Karnesi : kayıtlı veriden hesaplanır (AI yok), anında gelir.
 *  • Sayfa Analizi: URL girilir, o sayfa derinlemesine incelenir (1 AI çağrısı).
 *
 * DÜRÜSTLÜK NOTU arayüzde de yazar: E-E-A-T bir sıralama faktörü değildir ve
 * Google'ın verdiği bir puan yoktur. Gösterdiğimiz puan bizim gözlemlenebilir
 * sinyaller bileşkemizdir. Aksini iddia eden araç uyduruyor demektir.
 */
import { useState } from "react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ScanLine, ShieldCheck, Info } from "lucide-react";

type Durum = "gecti" | "eksik" | "kritik" | "bilinmiyor";
type Kontrol = {
  id: string; sutun: "deneyim" | "uzmanlik" | "otorite" | "guven";
  baslik: string; aranan: string; bulunan: string; durum: Durum;
  yapilacak?: string; ymylKritik?: boolean;
  bulgular?: { alinti: string; sorun?: string }[];
};
type Sutun = { sutun: string; ad: string; puan: number | null };
type Karne = {
  hazir: boolean; mesaj?: string; ymyl?: boolean; ymylSebep?: string;
  genelPuan?: number; sutunlar?: Sutun[]; kontroller?: Kontrol[];
  veriDurumu?: { taranmisSayfa: number; schemaDenetimi: boolean; backlinkDenetimi: boolean; aiGorunurluk: boolean };
};
type SayfaSonuc = {
  url: string; baslik: string; ymyl: boolean; genelPuan: number;
  sutunlar: Sutun[]; kontroller: Kontrol[]; aiOzet: string | null; aiCalisti: boolean; aiUsd: number;
};

const DURUM_STIL: Record<Durum, { etiket: string; sinif: string }> = {
  gecti:      { etiket: "TAMAM",  sinif: "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400" },
  eksik:      { etiket: "EKSİK",  sinif: "bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400" },
  kritik:     { etiket: "KRİTİK", sinif: "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-400" },
  bilinmiyor: { etiket: "VERİ YOK", sinif: "bg-accent text-muted-foreground" },
};
const puanRengi = (p: number) => (p >= 75 ? "text-success-600 dark:text-success-400" : p >= 50 ? "text-warning-700 dark:text-warning-400" : "text-error-600 dark:text-error-400");
// Kritik önce, sonra eksik, sonra veri yok, en son geçenler.
const SIRA: Record<Durum, number> = { kritik: 0, eksik: 1, bilinmiyor: 2, gecti: 3 };

function KontrolListesi({ kontroller }: { kontroller: Kontrol[] }) {
  const sirali = [...kontroller].sort((a, b) => SIRA[a.durum] - SIRA[b.durum]);
  return (
    <div className="divide-y divide-border">
      {sirali.map((k) => (
        <div key={k.id} className="py-2.5">
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${DURUM_STIL[k.durum].sinif}`}>
              {DURUM_STIL[k.durum].etiket}
            </span>
            <span className="text-sm font-medium">{k.baslik}</span>
            <span className="text-[11px] text-muted-foreground">{k.bulunan}</span>
          </div>
          {k.yapilacak && <p className="text-[12px] text-muted-foreground mt-1 pl-1">→ {k.yapilacak}</p>}
          {/* Alıntılar madde madde: "5 hata bulundu" demek yetmiyor, düzeltmeyi
              yapacak kişi hangi cümle olduğunu birebir görmeli. */}
          {k.bulgular && k.bulgular.length > 0 && (
            <ul className="mt-1.5 pl-1 space-y-1.5">
              {k.bulgular.map((b, i) => (
                <li key={i} className="text-[12px] border-l-2 border-border pl-2">
                  <span className="text-foreground">“{b.alinti}”</span>
                  {b.sorun && <span className="block text-[11px] text-muted-foreground mt-0.5">{b.sorun}</span>}
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-muted-foreground mt-0.5 pl-1 opacity-70">aranan: {k.aranan}</p>
        </div>
      ))}
    </div>
  );
}

function Sutunlar({ sutunlar, genelPuan }: { sutunlar: Sutun[]; genelPuan: number }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <Card className="border-brand-500/40"><CardContent className="p-3">
        <div className={`text-2xl font-bold tabular-nums ${puanRengi(genelPuan)}`}>{genelPuan}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">Genel</div>
      </CardContent></Card>
      {sutunlar.map((s) => (
        <Card key={s.sutun}><CardContent className="p-3">
          <div className={`text-2xl font-bold tabular-nums ${s.puan == null ? "text-muted-foreground" : puanRengi(s.puan)}`}>
            {s.puan ?? "—"}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{s.ad}</div>
        </CardContent></Card>
      ))}
    </div>
  );
}

export default function EeatAgent() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const [sekme, setSekme] = useState<"karne" | "sayfa">("karne");
  const [url, setUrl] = useState("");
  const [sonuc, setSonuc] = useState<SayfaSonuc | null>(null);
  const [busy, setBusy] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const { data: karne, isLoading } = useQuery({
    queryKey: ["eeat", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<Karne> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/eeat`); return r.ok ? await r.json() : { hazir: false, mesaj: "Karne alınamadı." }; }
      catch { return { hazir: false, mesaj: "Sunucuya ulaşılamadı." }; }
    },
  });

  const sayfaAnaliz = async () => {
    if (!activeDomainId || url.trim().length < 8) return;
    setBusy(true); setHata(null); setSonuc(null);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/eeat-page`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ url: url.trim() }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Analiz edilemedi");
      setSonuc(j);
    } catch (e) { setHata((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="px-4 sm:px-6 py-6 w-full max-w-(--breakpoint-2xl) mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-brand-500" /> E-E-A-T Ajanı</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Deneyim, Uzmanlık, Otorite ve Güven sinyallerini denetler.
        </p>
      </div>

      {/* DÜRÜSTLÜK NOTU — kalıcı, kapatılamaz. Uydurma kesinlik satmıyoruz. */}
      <Card className="border-border bg-accent/40"><CardContent className="p-3 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        <p className="text-[12px] text-muted-foreground">
          E-E-A-T bir sıralama faktörü <b>değildir</b> ve Google'ın verdiği bir puan yoktur; Arama Kalitesi Değerlendirici
          Kılavuzu'nda insan değerlendiricilerin kullandığı bir çerçevedir. Buradaki puan, o kılavuzun aradığı
          <b> gözlemlenebilir sinyallerin</b> bizim ölçümümüzdür — Google'ın notu değildir.
        </p>
      </CardContent></Card>

      <div className="flex gap-1 border-b border-border" role="tablist">
        {([["karne", "Site Karnesi"], ["sayfa", "Sayfa Analizi"]] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={sekme === k} onClick={() => setSekme(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              sekme === k ? "border-brand-500 text-brand-600 dark:text-brand-400" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* ── SİTE KARNESİ ── */}
      {sekme === "karne" && (<>
        {isLoading && <Card><CardContent className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin mr-1.5" /> Hesaplanıyor…</CardContent></Card>}
        {karne && !karne.hazir && (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{karne.mesaj}</CardContent></Card>
        )}
        {karne?.hazir && (<>
          {karne.ymyl && (
            <Card className="border-warning-500/40 bg-warning-50/50 dark:bg-warning-500/[0.08]"><CardContent className="p-3">
              <p className="text-[12px] text-warning-700 dark:text-warning-400">
                <b>YMYL sitesi</b> — {karne.ymylSebep}
              </p>
            </CardContent></Card>
          )}
          <Sutunlar sutunlar={karne.sutunlar ?? []} genelPuan={karne.genelPuan ?? 0} />
          <Card><CardContent className="p-4">
            <div className="font-semibold text-sm mb-1">Kontroller</div>
            <p className="text-[11px] text-muted-foreground mb-2">
              {karne.veriDurumu?.taranmisSayfa} taranmış sayfa üzerinden.
              {!karne.veriDurumu?.schemaDenetimi && " Schema denetimi yapılmadığı için bazı kontroller ölçülemedi."}
              {!karne.veriDurumu?.backlinkDenetimi && " Backlink denetimi yapılmamış."}
              {!karne.veriDurumu?.aiGorunurluk && " AI görünürlük ölçümü yapılmamış."}
            </p>
            <KontrolListesi kontroller={karne.kontroller ?? []} />
          </CardContent></Card>
        </>)}
      </>)}

      {/* ── SAYFA ANALİZİ ── */}
      {sekme === "sayfa" && (<>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <ScanLine className="w-4 h-4 text-brand-500" />
            <span className="font-semibold text-sm">Sayfa Analizi</span>
            <span className="text-[11px] text-muted-foreground">— tek sayfayı derinlemesine inceler</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input value={url} onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void sayfaAnaliz(); }}
              placeholder="https://siteniz.com/blog/yazi"
              className="flex-1 min-w-[240px] text-sm px-3 py-2 rounded-lg border bg-background" />
            <Button size="sm" onClick={() => void sayfaAnaliz()} disabled={busy || url.trim().length < 8}>
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analiz ediliyor…</> : "Analiz Et"}
            </Button>
          </div>
          {hata && <p className="text-[12px] text-error-600 dark:text-error-400 mt-2">{hata}</p>}
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Yalnız kendi sitenizin sayfaları analiz edilebilir. Yaklaşık 15-20 saniye sürer.
          </p>
        </CardContent></Card>

        {sonuc && (<>
          <Sutunlar sutunlar={sonuc.sutunlar} genelPuan={sonuc.genelPuan} />
          {sonuc.aiOzet && (
            <Card><CardContent className="p-4">
              <div className="font-semibold text-sm mb-1">Değerlendirme</div>
              <p className="text-[13px] text-muted-foreground">{sonuc.aiOzet}</p>
              <p className="text-[10px] text-muted-foreground mt-2">
                {sonuc.baslik} · {sonuc.ymyl ? "YMYL sayfa" : "standart sayfa"} · AI maliyeti ${sonuc.aiUsd}
              </p>
            </CardContent></Card>
          )}
          <Card><CardContent className="p-4">
            <div className="font-semibold text-sm mb-2">Kontroller</div>
            <KontrolListesi kontroller={sonuc.kontroller} />
          </CardContent></Card>
        </>)}
      </>)}
    </div>
  );
}
